'use client';

import { c as _c } from "react/compiler-runtime";
import { Button, Skeleton } from '@common/components';
import { useToast } from '@common/hooks';
import { useMutation, useSuspenseQuery } from '@tanstack/react-query';
import { Suspense, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { AuthMutation, UnauthQueryOptions } from '@/api';
import { CustomLink } from '@/app/_components/CustomLink';
import { useAdpopcornSspPlacementId } from '@/app/(allowed)/benefit/_hooks/useAdpopcornSspPlacementId';
import { useBenefitGate } from '@/app/(allowed)/benefit/_hooks/useBenefitGate';
import { useAffiliateMerchants } from '@/app/(allowed)/benefit/merchants/_hooks/useAffiliateMerchants';
import { PageNavigation } from '@/components/client';
import { QuerySuspenseErrorBoundary } from '@/components/organism/QuerySuspenseErrorBoundary';
import { CenteredDetailTemplate } from '@/components/template/CenteredDetailTemplate';
import { ROUTES } from '@/constants';
import { useHaptic } from '@/hooks/useHaptic';
import { useIsGuest } from '@/hooks/useIsGuest';
import { useOverlayHistory } from '@/hooks/useOverlayHistory';
import { useScrollTop } from '@/hooks/useScrollTop';
import { useAdpopcornSspFullscreenAd } from '@/modules/adpopcorn-ssp';
import { ADPOPCORN_SSP_MODULE } from '@/modules/wello-app';
import { useDeviceInfoStore } from '@/stores/DeviceStore';
import { useGlobalStore } from '@/stores/GlobalStore';
import { AD_TRIGGER_COOLDOWN_NOTICE, AD_TRIGGER_PENDING_NOTICE } from '../../../_utils/adTriggerNotice';
import { ROULETTE_MAX_RETRY_COUNT } from '../_constants';
import { type RouletteDetail, useRouletteDetail } from '../_hooks/useRouletteDetail';
import { type RouletteSpinResult, useRouletteSpin } from '../_hooks/useRouletteSpin';
import { useRouletteStrip } from '../_hooks/useRouletteStrip';
import { createResumedRouletteSpin, resolveRouletteAvailability, resolveRouletteLandingIndex, resolveRouletteRetryType } from '../_utils/resolveRouletteState';
import { RouletteConfirmModal } from './RouletteConfirmModal';
import { RouletteItemStrip } from './RouletteItemStrip';
import { RouletteNotices } from './RouletteNotices';
import { RouletteResultScreen } from './RouletteResultScreen';
import { RouletteSpinBackGuard } from './RouletteSpinBackGuard';
type RouletteView = {
  step: 'idle';
} | {
  step: 'spinning';
} | {
  step: 'result';
  spin: RouletteSpinResult;
};
const CoupangEarnLinkView = t0 => {
  const $ = _c(2);
  const {
    href
  } = t0;
  let t1;
  if ($[0] !== href) {
    t1 = <Button asChild={true} className="w-full"><CustomLink href={href}>쿠팡에서 쇼핑하고 제휴포인트 쌓기</CustomLink></Button>;
    $[0] = href;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
};
const CoupangEarnLink = () => {
  const $ = _c(6);
  const {
    data: merchants
  } = useAffiliateMerchants();
  let T0;
  let t0;
  if ($[0] !== merchants) {
    const partnerPointMerchant = merchants.find(_temp);
    T0 = CoupangEarnLinkView;
    t0 = partnerPointMerchant ? ROUTES.BENEFIT_MERCHANT_DETAIL.pathname({
      merchantCode: partnerPointMerchant.merchantCode
    }) : ROUTES.BENEFIT_MERCHANTS.pathname;
    $[0] = merchants;
    $[1] = T0;
    $[2] = t0;
  } else {
    T0 = $[1];
    t0 = $[2];
  }
  let t1;
  if ($[3] !== T0 || $[4] !== t0) {
    t1 = <T0 href={t0} />;
    $[3] = T0;
    $[4] = t0;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  return t1;
};
const InsufficientBalanceDim = t0 => {
  const $ = _c(3);
  const {
    onClose
  } = t0;
  useOverlayHistory(true, onClose);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <p id="roulette-insufficient-title" className="flex flex-1 items-center justify-center whitespace-pre-line break-keep px-40 text-center font-700 text-Gray-white typo-body1">{"\uC81C\uD734\uD3EC\uC778\uD2B8\uAC00 \uBD80\uC871\uD574\n\uB8F0\uB81B\uC5D0 \uCC38\uC5EC\uD560 \uC218 \uC5C6\uC5B4\uC694."}</p>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== onClose) {
    t2 = <div role="alertdialog" aria-modal={true} aria-labelledby="roulette-insufficient-title" className="fixed inset-0 z-40 flex flex-col bg-Gray-black/50">{t1}<div className="mx-auto w-full max-w-[750px] px-20 pb-20"><Button className="w-full" onClick={onClose}>닫기</Button></div></div>;
    $[1] = onClose;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
};
const RouletteHeading = () => {
  const $ = _c(2);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <h1 className="font-700 text-Gray-900 typo-body2">원하는 쿠폰에 도전해보세요!</h1>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div className="flex flex-col gap-4 px-24 pt-52 mobile-tablet:px-20 mobile-tablet:pt-20">{t0}<p className="font-400 text-Gray-600 typo-caption1">룰렛을 돌리면 여러 쿠폰 중{" "}<span className="font-600">하나의 모바일 쿠폰</span>이 랜덤으로 당첨돼요!</p></div>;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
};
const RouletteLoading = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div aria-hidden={true} className="flex gap-12 overflow-hidden px-24 mobile-tablet:px-20">{Array.from({
        length: 3
      }, _temp2)}</div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
};
interface RouletteBoardProps {
  detail: RouletteDetail;
  isGuest: boolean;
  onAppOnlyEntry?: () => void;
}
interface PendingAdRetry {
  rouletteEntryId: string;
  sspHistoryId: string;
}
const RouletteBoard = t0 => {
  const $ = _c(106);
  const {
    detail,
    isGuest,
    onAppOnlyEntry
  } = t0;
  const {
    toast
  } = useToast();
  const {
    triggerGate,
    gateModals
  } = useBenefitGate();
  const {
    triggerHaptic
  } = useHaptic();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      step: "idle"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [view, setView] = useState(t1);
  let t2;
  if ($[1] !== detail.inProgressEntry) {
    t2 = () => detail.inProgressEntry ? createResumedRouletteSpin(detail.inProgressEntry) : null;
    $[1] = detail.inProgressEntry;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const [resumableSpin, setResumableSpin] = useState(t2);
  const [pendingAdRetry, setPendingAdRetry] = useState(null);
  const [isConfirmOpened, setIsConfirmOpened] = useState(false);
  let t3;
  if ($[3] !== detail) {
    t3 = resolveRouletteAvailability(detail);
    $[3] = detail;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const availability = t3;
  const isInsufficient = availability === "INSUFFICIENT_BALANCE";
  const [isInsufficientDimOpened, setIsInsufficientDimOpened] = useState(isInsufficient && !resumableSpin);
  const osType = useDeviceInfoStore(_temp3);
  const setIsShowAppInstallConfirmModal = useGlobalStore(_temp4);
  let t4;
  if ($[5] !== osType || $[6] !== setIsShowAppInstallConfirmModal || $[7] !== toast) {
    t4 = () => {
      if (osType === "mac" || osType === "windows") {
        toast({
          message: "\uC571\uC5D0\uC11C\uB9CC \uCC38\uC5EC\uD560 \uC218 \uC788\uC5B4\uC694",
          type: "info"
        });
        return;
      }
      setIsShowAppInstallConfirmModal(true);
    };
    $[5] = osType;
    $[6] = setIsShowAppInstallConfirmModal;
    $[7] = toast;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const handleAppOnly = t4;
  const {
    startEntry,
    startRetry,
    isSpinning
  } = useRouletteSpin(detail.rouletteBrandId, handleAppOnly);
  const strip = useRouletteStrip(detail.itemList.length);
  const placementId = useAdpopcornSspPlacementId(ADPOPCORN_SSP_MODULE.REWARD_VIDEO);
  let t5;
  if ($[9] !== triggerHaptic) {
    t5 = spin => {
      setResumableSpin(null);
      triggerHaptic("success");
      setView({
        step: "result",
        spin
      });
    };
    $[9] = triggerHaptic;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const showResult = t5;
  let t6;
  if ($[11] !== detail.itemList || $[12] !== showResult || $[13] !== strip) {
    t6 = spin_0 => {
      strip.land(resolveRouletteLandingIndex(detail.itemList, spin_0), () => showResult(spin_0));
    };
    $[11] = detail.itemList;
    $[12] = showResult;
    $[13] = strip;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  const handleSpun = t6;
  let t7;
  if ($[15] !== strip) {
    t7 = () => {
      strip.stop();
      setView({
        step: "idle"
      });
    };
    $[15] = strip;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  const handleSpinFailed = t7;
  let t8;
  if ($[17] !== strip || $[18] !== triggerHaptic) {
    t8 = () => {
      triggerHaptic();
      setView({
        step: "spinning"
      });
      strip.start();
    };
    $[17] = strip;
    $[18] = triggerHaptic;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  const beginSpin = t8;
  let t9;
  if ($[20] !== beginSpin || $[21] !== handleSpun || $[22] !== resumableSpin || $[23] !== startRetry || $[24] !== strip) {
    t9 = (previousSpin, sspHistoryId) => {
      beginSpin();
      startRetry(previousSpin.rouletteEntryId, sspHistoryId, {
        onSpun: spin_1 => {
          setPendingAdRetry(null);
          handleSpun(spin_1);
        },
        onFailed: () => {
          strip.stop();
          setView(previousSpin === resumableSpin ? {
            step: "idle"
          } : {
            step: "result",
            spin: previousSpin
          });
        }
      });
    };
    $[20] = beginSpin;
    $[21] = handleSpun;
    $[22] = resumableSpin;
    $[23] = startRetry;
    $[24] = strip;
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  const retryEntry = t9;
  let t10;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = AuthMutation["post:/wello/v2/auth/external/adpopcorn-ssp"]();
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  const adHistory = useMutation(t10);
  let t11;
  if ($[27] !== adHistory || $[28] !== placementId || $[29] !== resumableSpin || $[30] !== retryEntry || $[31] !== toast || $[32] !== view.spin || $[33] !== view.step) {
    t11 = {
      autoShow: true,
      onCompleted: () => {
        const previousSpin_0 = view.step === "result" ? view.spin : resumableSpin;
        if (!previousSpin_0) {
          return;
        }
        adHistory.mutate({
          body: {
            placementId,
            sspType: "REWARDED_VIDEO",
            rouletteYN: true
          }
        }, {
          onSuccess: response => {
            const sspHistoryId_0 = response.context?.sspHistoryId;
            if (!sspHistoryId_0) {
              toast({
                message: "\uC7A0\uC2DC \uD6C4 \uB2E4\uC2DC \uC2DC\uB3C4\uD574 \uC8FC\uC138\uC694",
                type: "fail"
              });
              return;
            }
            setPendingAdRetry({
              rouletteEntryId: previousSpin_0.rouletteEntryId,
              sspHistoryId: sspHistoryId_0
            });
            retryEntry(previousSpin_0, sspHistoryId_0);
          },
          onError: () => {
            toast({
              message: "\uC7A0\uC2DC \uD6C4 \uB2E4\uC2DC \uC2DC\uB3C4\uD574 \uC8FC\uC138\uC694",
              type: "fail"
            });
          }
        });
      }
    };
    $[27] = adHistory;
    $[28] = placementId;
    $[29] = resumableSpin;
    $[30] = retryEntry;
    $[31] = toast;
    $[32] = view.spin;
    $[33] = view.step;
    $[34] = t11;
  } else {
    t11 = $[34];
  }
  const {
    load: loadAd,
    phase: adPhase
  } = useAdpopcornSspFullscreenAd(ADPOPCORN_SSP_MODULE.REWARD_VIDEO, t11);
  let t12;
  if ($[35] !== loadAd || $[36] !== pendingAdRetry || $[37] !== placementId || $[38] !== retryEntry) {
    t12 = spin_2 => {
      if (pendingAdRetry?.rouletteEntryId === spin_2.rouletteEntryId) {
        retryEntry(spin_2, pendingAdRetry.sspHistoryId);
        return;
      }
      loadAd(placementId);
    };
    $[35] = loadAd;
    $[36] = pendingAdRetry;
    $[37] = placementId;
    $[38] = retryEntry;
    $[39] = t12;
  } else {
    t12 = $[39];
  }
  const handleAdRetry = t12;
  let t13;
  if ($[40] !== isGuest || $[41] !== onAppOnlyEntry || $[42] !== triggerGate) {
    t13 = () => {
      if (onAppOnlyEntry) {
        onAppOnlyEntry();
        return;
      }
      if (isGuest) {
        triggerGate(["login"], () => setIsConfirmOpened(true));
        return;
      }
      setIsConfirmOpened(true);
    };
    $[40] = isGuest;
    $[41] = onAppOnlyEntry;
    $[42] = triggerGate;
    $[43] = t13;
  } else {
    t13 = $[43];
  }
  const handleEntryClick = t13;
  let t14;
  if ($[44] !== beginSpin || $[45] !== handleSpinFailed || $[46] !== handleSpun || $[47] !== startEntry) {
    t14 = () => {
      setIsConfirmOpened(false);
      beginSpin();
      startEntry({
        onSpun: handleSpun,
        onFailed: handleSpinFailed
      });
    };
    $[44] = beginSpin;
    $[45] = handleSpinFailed;
    $[46] = handleSpun;
    $[47] = startEntry;
    $[48] = t14;
  } else {
    t14 = $[48];
  }
  const handleConfirm = t14;
  let t15;
  if ($[49] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = spin_3 => {
      setView({
        step: "idle"
      });
      setResumableSpin(resolveRouletteRetryType(spin_3) === "NONE" ? null : spin_3);
    };
    $[49] = t15;
  } else {
    t15 = $[49];
  }
  const handleResultClose = t15;
  const isBusy = view.step === "spinning" || isSpinning;
  let t16;
  if ($[50] !== availability || $[51] !== resumableSpin) {
    t16 = resumableSpin && (availability === "DRAWABLE" || availability === "INSUFFICIENT_BALANCE") ? resolveRouletteRetryType(resumableSpin) : "NONE";
    $[50] = availability;
    $[51] = resumableSpin;
    $[52] = t16;
  } else {
    t16 = $[52];
  }
  const resumableRetryType = t16;
  const adNotice = adPhase === "pending" ? AD_TRIGGER_PENDING_NOTICE : adPhase === "cooldown" ? AD_TRIGGER_COOLDOWN_NOTICE : null;
  let t17;
  if ($[53] !== detail.itemList || $[54] !== strip.copyCount || $[55] !== strip.isLooping || $[56] !== strip.landedIndex || $[57] !== strip.viewportRef || $[58] !== strip.x) {
    t17 = <RouletteItemStrip items={detail.itemList} x={strip.x} viewportRef={strip.viewportRef} isLooping={strip.isLooping} copyCount={strip.copyCount} landedIndex={strip.landedIndex} />;
    $[53] = detail.itemList;
    $[54] = strip.copyCount;
    $[55] = strip.isLooping;
    $[56] = strip.landedIndex;
    $[57] = strip.viewportRef;
    $[58] = strip.x;
    $[59] = t17;
  } else {
    t17 = $[59];
  }
  let t18;
  if ($[60] !== detail.blankProbability || $[61] !== detail.itemList) {
    t18 = <RouletteNotices items={detail.itemList} blankProbability={detail.blankProbability} />;
    $[60] = detail.blankProbability;
    $[61] = detail.itemList;
    $[62] = t18;
  } else {
    t18 = $[62];
  }
  let t19;
  if ($[63] !== t17 || $[64] !== t18) {
    t19 = <div className="flex flex-col gap-12 pb-20">{t17}{t18}</div>;
    $[63] = t17;
    $[64] = t18;
    $[65] = t19;
  } else {
    t19 = $[65];
  }
  let t20;
  if ($[66] !== adHistory || $[67] !== adNotice || $[68] !== adPhase || $[69] !== availability || $[70] !== detail.requiredPoint || $[71] !== handleAdRetry || $[72] !== handleEntryClick || $[73] !== isBusy || $[74] !== isInsufficient || $[75] !== resumableRetryType || $[76] !== resumableSpin || $[77] !== retryEntry) {
    t20 = <footer className="fixed inset-x-0 bottom-0 z-[1] bg-gradient-to-t from-Gray-white from-60% to-Gray-white/0"><div className="mx-auto flex w-full max-w-[750px] flex-col items-center gap-8 px-20 pb-20 pt-32 xl:max-w-[440px]">{resumableSpin && resumableRetryType !== "NONE" ? <><p className="font-600 text-Gray-700 typo-caption1">{resumableRetryType === "AD" && adNotice ? adNotice : `남은 재도전 기회 ${resumableSpin.remainingRetryCount}/${ROULETTE_MAX_RETRY_COUNT}`}</p>{resumableRetryType === "FREE" ? <Button className="w-full" loading={isBusy} disabled={isBusy} onClick={() => retryEntry(resumableSpin)}>한 번 더 돌리기</Button> : <Button className="w-full" loading={isBusy || adPhase === "pending" || adHistory.isPending} disabled={isBusy || adPhase === "cooldown"} onClick={() => handleAdRetry(resumableSpin)}>광고 보고 한 번 더 참여하기</Button>}</> : isInsufficient && !isBusy ? <ErrorBoundary fallback={<CoupangEarnLinkView href={ROUTES.BENEFIT_MERCHANTS.pathname} />}><Suspense fallback={<CoupangEarnLinkView href={ROUTES.BENEFIT_MERCHANTS.pathname} />}><CoupangEarnLink /></Suspense></ErrorBoundary> : <><p className="font-600 text-Gray-700 typo-caption1">{availability === "NO_ITEM" ? "\uC9C0\uAE08\uC740 \uCC38\uC5EC\uD560 \uC218 \uC788\uB294 \uCFE0\uD3F0\uC774 \uC5C6\uC5B4\uC694" : availability === "UNRECOVERED" ? "\uC815\uC0B0 \uC911\uC778 \uAE08\uC561\uC774 \uC788\uC5B4 \uC9C0\uAE08\uC740 \uC0AC\uC6A9\uD560 \uC218 \uC5C6\uC5B4\uC694" : <>1회 참여{" "}<span className="text-B-500">{detail.requiredPoint.toLocaleString()}P</span></>}</p><Button className="w-full" loading={isBusy} disabled={availability === "NO_ITEM" || availability === "UNRECOVERED" || isBusy} onClick={handleEntryClick}>룰렛 참여하기</Button></>}</div></footer>;
    $[66] = adHistory;
    $[67] = adNotice;
    $[68] = adPhase;
    $[69] = availability;
    $[70] = detail.requiredPoint;
    $[71] = handleAdRetry;
    $[72] = handleEntryClick;
    $[73] = isBusy;
    $[74] = isInsufficient;
    $[75] = resumableRetryType;
    $[76] = resumableSpin;
    $[77] = retryEntry;
    $[78] = t20;
  } else {
    t20 = $[78];
  }
  let t21;
  if ($[79] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = () => setIsConfirmOpened(false);
    $[79] = t21;
  } else {
    t21 = $[79];
  }
  let t22;
  if ($[80] !== detail.balance || $[81] !== detail.requiredPoint || $[82] !== handleConfirm || $[83] !== isConfirmOpened) {
    t22 = <RouletteConfirmModal opened={isConfirmOpened} requiredPoint={detail.requiredPoint} balance={detail.balance} onClose={t21} onConfirm={handleConfirm} />;
    $[80] = detail.balance;
    $[81] = detail.requiredPoint;
    $[82] = handleConfirm;
    $[83] = isConfirmOpened;
    $[84] = t22;
  } else {
    t22 = $[84];
  }
  let t23;
  if ($[85] !== isInsufficientDimOpened) {
    t23 = isInsufficientDimOpened ? <InsufficientBalanceDim onClose={() => setIsInsufficientDimOpened(false)} /> : null;
    $[85] = isInsufficientDimOpened;
    $[86] = t23;
  } else {
    t23 = $[86];
  }
  let t24;
  if ($[87] !== view.step) {
    t24 = view.step === "spinning" ? <RouletteSpinBackGuard /> : null;
    $[87] = view.step;
    $[88] = t24;
  } else {
    t24 = $[88];
  }
  let t25;
  if ($[89] !== adHistory || $[90] !== adPhase || $[91] !== detail.name || $[92] !== handleAdRetry || $[93] !== isSpinning || $[94] !== retryEntry || $[95] !== view.spin || $[96] !== view.step) {
    t25 = view.step === "result" ? <RouletteResultScreen spin={view.spin} brandName={detail.name} adPhase={adPhase} isRetrying={isSpinning || adHistory.isPending} onFreeRetry={() => retryEntry(view.spin)} onAdRetry={() => handleAdRetry(view.spin)} onClose={() => handleResultClose(view.spin)} /> : null;
    $[89] = adHistory;
    $[90] = adPhase;
    $[91] = detail.name;
    $[92] = handleAdRetry;
    $[93] = isSpinning;
    $[94] = retryEntry;
    $[95] = view.spin;
    $[96] = view.step;
    $[97] = t25;
  } else {
    t25 = $[97];
  }
  let t26;
  if ($[98] !== gateModals || $[99] !== t19 || $[100] !== t20 || $[101] !== t22 || $[102] !== t23 || $[103] !== t24 || $[104] !== t25) {
    t26 = <>{t19}{t20}{gateModals}{t22}{t23}{t24}{t25}</>;
    $[98] = gateModals;
    $[99] = t19;
    $[100] = t20;
    $[101] = t22;
    $[102] = t23;
    $[103] = t24;
    $[104] = t25;
    $[105] = t26;
  } else {
    t26 = $[105];
  }
  return t26;
};
const MemberRouletteBoard = t0 => {
  const $ = _c(3);
  const {
    rouletteBrandId
  } = t0;
  const {
    data: detail
  } = useRouletteDetail(rouletteBrandId);
  if (!detail) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <RouletteLoadError />;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  let t1;
  if ($[1] !== detail) {
    t1 = <RouletteBoard detail={detail} isGuest={false} />;
    $[1] = detail;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
};
const ReadOnlyRouletteBoard = t0 => {
  const $ = _c(8);
  const {
    rouletteBrandId,
    onAppOnlyEntry
  } = t0;
  let t1;
  if ($[0] !== rouletteBrandId) {
    t1 = UnauthQueryOptions["get:/wello/v1/allows/affiliate/partner-points/roulettes/{rouletteBrandId}"]({
      path: {
        rouletteBrandId
      }
    });
    $[0] = rouletteBrandId;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = {
      ...t1,
      select: _temp5
    };
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const {
    data: detail
  } = useSuspenseQuery(t2);
  if (!detail) {
    let t3;
    if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = <RouletteLoadError />;
      $[4] = t3;
    } else {
      t3 = $[4];
    }
    return t3;
  }
  let t3;
  if ($[5] !== detail || $[6] !== onAppOnlyEntry) {
    t3 = <RouletteBoard detail={detail} isGuest={true} onAppOnlyEntry={onAppOnlyEntry} />;
    $[5] = detail;
    $[6] = onAppOnlyEntry;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return t3;
};
const RouletteLoadError = t0 => {
  const $ = _c(5);
  const {
    onRetry
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <p className="font-500 text-Gray-600 typo-body4">불러오지 못했어요</p>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== onRetry) {
    t2 = onRetry ? <Button theme="sub-line" dimensions="H40" onClick={onRetry}>다시 시도</Button> : null;
    $[1] = onRetry;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== t2) {
    t3 = <div className="flex min-h-240 flex-col items-center justify-center gap-12 px-20">{t1}{t2}</div>;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  return t3;
};
export const RouletteScreen = t0 => {
  const $ = _c(6);
  const {
    rouletteBrandId,
    isGuest: serverIsGuest
  } = t0;
  useScrollTop();
  const isGuest = useIsGuest(serverIsGuest);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <PageNavigation backButtonType="exit" fallbackHref={ROUTES.BENEFIT_PARTNER_POINT_SHOP.pathname} hideHomeButton={true} className="bg-B-25" />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <RouletteHeading />;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <RouletteLoading />;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== isGuest || $[4] !== rouletteBrandId) {
    t4 = <CenteredDetailTemplate fullWidth={true} className="min-h-full break-keep bg-gradient-to-b from-B-25 to-Gray-white pb-160"><div className="mx-auto flex w-full max-w-[750px] flex-col">{t1}<div className="flex flex-col gap-16">{t2}<QuerySuspenseErrorBoundary suspenseFallback={t3} errorFallback={_temp6}>{isGuest ? <ReadOnlyRouletteBoard rouletteBrandId={rouletteBrandId} /> : <MemberRouletteBoard rouletteBrandId={rouletteBrandId} />}</QuerySuspenseErrorBoundary></div></div></CenteredDetailTemplate>;
    $[3] = isGuest;
    $[4] = rouletteBrandId;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  return t4;
};
function _temp(merchant) {
  return merchant.rewardCurrencyType === "AFFILIATE_PARTNER_POINT";
}
function _temp2(_, index) {
  return <Skeleton key={index} className="h-136 w-132 shrink-0 rounded-xxlarge" />;
}
function _temp3(state) {
  return state.osType;
}
function _temp4(state_0) {
  return state_0.setIsShowAppInstallConfirmModal;
}
function _temp5(response) {
  return response.context;
}
function _temp6(t0) {
  const {
    resetErrorBoundary
  } = t0;
  return <RouletteLoadError onRetry={resetErrorBoundary} />;
}